/**
 * The patients domain's FLAG MODULE (ruling 36 as amended 2026-08-29; converted
 * in flags fan-out batch 2).
 *
 * ONE DOOR, ONE FILE. The `defineFeatureFlag` descriptors and the named getters
 * that read them are CO-LOCATED here, and `@heidi/patients/flags` publishes
 * both. `/contributions` carries nothing for flags — this domain HAS such a
 * door, holding twelve `defineAnalyticsEvent` declarations, and none of what is
 * below belongs in it. That is the arrangement the 2026-08-29 amendment
 * supersedes and the reason this file exists rather than a `feature-flags.ts`
 * beside those event names.
 *
 * ONE MINTING SITE PER FLAG, made structural rather than conventional by
 * co-location: each getter reads `descriptor.key` / `descriptor.defaultValue`
 * from the declaration directly above it, so the pair cannot drift, and
 * `flags.test.ts` asserts each key appears exactly once in this file and that no
 * read takes a string literal — the only assertion that can tell a descriptor
 * read from a hardcoded string that happens to be equal today. Flag keys have no
 * cross-site-identity guard (cache keys do), so a second spelling would be
 * invisible to every other gate in the repo.
 *
 * THE DOOR IS HEADLESS, guarded transitively rather than by inspection.
 * `scripts/monorepo-migration/lib/package-ui-zone.ts` admits
 * `@heidi/runtime/feature-flags` ONLY from this file out of everything under
 * `packages/patients/src`; `scripts/check-flag-getter-doors.test.ts` walks this
 * module's graph ACROSS package boundaries and refuses `@heidi/design-system`,
 * this package's own `/ui` zone and the app tree anywhere in it, and separately
 * proves the door imports in a bare Node process with no DOM and no side
 * effects.
 *
 * PACKAGE UI IMPORTS THESE RELATIVELY (`../flags`), never through the door — a
 * package that imports itself by name resolves through node_modules and reads as
 * an undeclared external dependency. The app barrel `@/features/flags`
 * re-exports the getters from `@heidi/patients/flags`, so its importers — 37
 * production files across the five names below — and their `jest.mock`
 * literals are untouched.
 *
 * CROSS-ROOT READS STAY WHERE THEY ARE, and the biggest one is worth naming
 * because it looks like a mistake: five of the seven
 * `useIsPatientDataRetentionEnabled` consumers sit under
 * `src/features/settings/**` — the user, team and organisation data-management
 * screens plus the notifications form — rather than under a patients root. Those
 * are workspace-settings SURFACES reading a patients-owned flag, which is a
 * legal cross-root read of a name the barrel re-exports. Ownership follows the
 * flag's namespace and the story it gates (patient retention, the TTL sweeper),
 * not the directory its widest reader happens to live in.
 *
 * WHAT DOES NOT BELONG HERE — one of this domain's six app-barrel getters is
 * NOT below, and it is named rather than silently skipped.
 * `useIsPatientSummaryEnabled` reads `release-patient-summary` through
 * `useFeatureFlagEnabled` from `@/app-shell/flags/posthog-compat`, which resolves
 * from posthog-js on cloud and only falls back to OpenFeature on the airgapped
 * build. `@heidi/runtime/feature-flags` carries no PostHog surface, so rewriting
 * it onto the door would change WHICH PROVIDER answers — a behaviour change
 * wearing a refactor's clothes, and `monorepo-wave-1-p3-feature-flags` Z4 is the
 * standing rule against it. It moves when the flag is steered to Flagsmith, not
 * before, and its classification is named at its app-barrel site too.
 */
import {
  defineFeatureFlag,
  readFlag,
  useBooleanFlag,
} from '@heidi/runtime/feature-flags';

// ---------------------------------------------------------------------------
// The declarations. `key` is a FROZEN EXTERNAL IDENTITY — Flagsmith targets the
// flag by it, so renaming one is switching flags, never a refactor.
//
// `builds: 'all'` on all five, measured rather than assumed. The surfaces these
// gate are the sidebar Patients entry, the `/patients` page and drawer, the
// patient-deletion modals, the user/team/org data-management settings, the
// find-or-link combobox across the session header and session list, and the
// AN-ACC verify group; not one of them carries an `isOnPrem` gate, and neither
// does the account settings tree they hang off. Omitting the field would
// silently take the D6 default (`'cloud-only'`) and withhold from the appliance
// surfaces it already ships.
// Contrast `@heidi/calls`, which omits it on the opposite finding: its telephony
// path refuses the airgapped build in its own code.
// ---------------------------------------------------------------------------

/**
 * The standalone Patients surface (Phase 0): the sidebar nav item, the
 * `/patients` page and `/patients/[id]`, and the shared patient drawer. The
 * widest-consumed name in this batch — ten production files, from
 * `GlobalModalsWrapper` and the session-page context tab through to the
 * file-upload bar.
 */
export const patientListFlag = defineFeatureFlag({
  key: 'release-scribe-enable-patient-list',
  defaultValue: false,
  description: 'Standalone Patients surface — nav item, page and patient drawer',
  builds: 'all',
});

/**
 * The user-facing "uncouple" behaviour on patient deletion: when on, the
 * single-patient delete modal becomes a force-pick (keep sessions vs delete
 * sessions) and the same choice surfaces on the Delete All Patients flow.
 * Today's cascade behaviour is what OFF preserves.
 *
 * Intentionally independent of both siblings here. `release-scribe-enable-patient-list`
 * gates the surface and `release-scribe-patient-data-retention` gates the
 * autonomous TTL sweeper; user-controlled deletion ramps on its own cadence.
 */
export const uncouplePatientSessionDeletionFlag = defineFeatureFlag({
  key: 'release-scribe-uncouple-patient-session-deletion',
  defaultValue: false,
  description: 'Force-pick keep/delete sessions on patient deletion',
  builds: 'all',
});

/**
 * The automated patient-retention story: the TTL sweeper that deletes patients
 * past a per-user threshold, the data-management page that configures it, and
 * the org-admin override that lets teams force a retention policy onto members.
 *
 * Higher blast radius than the uncouple flag — autonomous deletion rather than
 * user-triggered — so it ramps separately. Kill-switch env vars on the backend
 * cron sit beneath this flag as belt and braces.
 */
export const patientDataRetentionFlag = defineFeatureFlag({
  key: 'release-scribe-patient-data-retention',
  defaultValue: false,
  description: 'Patient retention TTL sweeper, its settings UI and the org override',
  builds: 'all',
});

/**
 * The find-or-link patient combobox: the in-session header search, the
 * session-list link popover, the row and bulk-bar link avatars, and the
 * local-first roster layer inside the search.
 *
 * THE FOURTH DESCRIPTOR, added after the fan-out batch rather than in it, and
 * the reason it was left behind is worth recording: ruling 36's batch 2
 * converted the three getters whose bodies were already
 * `useBooleanFlagValue` + a `process.env` branch, and read this one's body as
 * a fourth of the same shape without moving it. It is — a plain
 * `useBooleanFlagValue('release-scribe-enable-patient-find-or-link', false)`
 * with no PostHog composition and no `posthog-compat` read — so it converts on
 * exactly the recipe above it. `builds: 'all'` on the same measurement as its
 * three siblings: none of the twelve production consumers carries an
 * `isOnPrem` gate, so taking D6's `'cloud-only'` default by omission would
 * withhold in-session patient linking from the appliance.
 */
export const findOrLinkPatientFlag = defineFeatureFlag({
  key: 'release-scribe-enable-patient-find-or-link',
  defaultValue: false,
  description: 'Find-or-link patient combobox across the session header and list',
  builds: 'all',
});

/**
 * The AN-ACC reassessment evidence-prep PoC: the Heidi Verify AN-ACC finding
 * group and the editable AN-ACC class field. The one key here that does not open
 * `release-scribe-` — it is the backend's spelling and travels verbatim.
 */
export const anaccVerifyFlag = defineFeatureFlag({
  key: 'release-anacc-verify',
  defaultValue: false,
  description: 'AN-ACC verify finding group and the editable AN-ACC class field',
  builds: 'all',
});

// ---------------------------------------------------------------------------
// The getters. Each reads the pair off the descriptor above it.
//
// THE forceAll BRANCH COLLAPSED ON ALL THREE, and it is behaviour-preserving
// rather than a dropped feature — this domain is where that shape was densest,
// so it is stated once here instead of three times below. Every app-side body
// was a `useBooleanFlagValue` read wrapped in
// `if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return
// true;`, a hand-rolled dev escape hatch whose own comment on the patient-list
// getter asked for it to be removed "once an OpenFeature-wide force-all provider
// exists". One exists: `flags.ts` may not read `process.env` and does not need
// to, because `useBooleanFlag` returns
// `getFeatureFlagsConfig().forceEnableAll || value` and the app injects that
// boolean from the SAME env var at `src/composition/wiring/runtime-wiring.ts`. The manual
// read disappears; the behaviour does not. `flags.test.ts` asserts the absence
// rather than describing it.
//
// THE FOURTH GETTER IS THE OTHER HALF OF THAT DELTA, and it points the opposite
// way, which is why it is stated separately instead of folded into the count
// above. `useIsFindOrLinkPatientEnabled` carried NO hand-rolled branch, so it
// has no collapse to disclose — it GAINS the injected `forceEnableAll`
// override that its `useBooleanFlagValue` call did not honour. Same shape as
// every getter templates and workspace moved in batch 2; a gain rather than a
// risk, and the knob is dev-only (`src/composition/wiring/runtime-wiring.ts` injects it from
// `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS`).
// ---------------------------------------------------------------------------

/** The standalone Patients surface — nav item, page and patient drawer. */
export const useIsPatientListEnabled = (): boolean =>
  useBooleanFlag(patientListFlag.key, patientListFlag.defaultValue);

/** The force-pick keep/delete-sessions behaviour on patient deletion. */
export const useIsUncouplePatientSessionDeletionEnabled = (): boolean =>
  useBooleanFlag(
    uncouplePatientSessionDeletionFlag.key,
    uncouplePatientSessionDeletionFlag.defaultValue
  );

/** The patient-retention sweeper, its settings UI and the org-admin override. */
export const useIsPatientDataRetentionEnabled = (): boolean =>
  useBooleanFlag(patientDataRetentionFlag.key, patientDataRetentionFlag.defaultValue);

/** The find-or-link patient combobox across the session header and session list. */
export const useIsFindOrLinkPatientEnabled = (): boolean =>
  useBooleanFlag(findOrLinkPatientFlag.key, findOrLinkPatientFlag.defaultValue);

/** The AN-ACC verify finding group and the editable AN-ACC class field. */
export const useIsAnaccVerifyEnabled = (): boolean =>
  useBooleanFlag(anaccVerifyFlag.key, anaccVerifyFlag.defaultValue);

/**
 * Patient list server-side pagination and search cutover. When on, the patient
 * list fetches a single page at a time and searches via the `q` field in the
 * POST body to `POST /v2.1/patient-profiles/` — NOT a URL query param, so that
 * patient-name search terms stay out of URL access logs (PHI constraint).
 *
 * Independent of `release-scribe-enable-patient-list` — that flag gates the
 * surface; this flag gates how the surface fetches its data.
 *
 * `builds: 'all'` — the patient list carries no `isOnPrem` gate.
 */
export const patientsServerPaginationFlag = defineFeatureFlag({
  key: 'release-patients-server-pagination',
  defaultValue: false,
  description: 'Patient list server-side pagination and search cutover',
  builds: 'all',
});

/** Patient list server-side pagination and search. */
export const useIsPatientsServerPaginationEnabled = (): boolean =>
  useBooleanFlag(
    patientsServerPaginationFlag.key,
    patientsServerPaginationFlag.defaultValue
  );

/**
 * Post-session patient-context field extraction and the Data Management
 * opt-out. `builds: 'all'` — none of the readers carry an on-prem gate.
 */
export const patientContextAutoExtractFlag = defineFeatureFlag({
  key: 'release-patient-context-auto-extract',
  defaultValue: false,
  description:
    'Post-session patient-context field extraction and the Data Management opt-out',
  builds: 'all',
});

/**
 * Deliberately imperative. Callers run outside React, so this is `readFlag`
 * and not a hook.
 */
export const isPatientContextAutoExtractEnabled = (): boolean =>
  readFlag(patientContextAutoExtractFlag.key, patientContextAutoExtractFlag.defaultValue);
